import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Header,
  Icon,
  Label,
  Table,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import {
  API,
  isAdmin,
  showError,
  timestamp2string,
} from '../../helpers';
import { renderColorLabel, renderQuota } from '../../helpers/render';

// AlphaGate B13: 把 归属地 各字段拼成一行可读文本; 全空返回 '-'。
function renderGeoText(row) {
  const parts = [row.country, row.region, row.city].filter(Boolean);
  let text = parts.join(' · ');
  if (row.isp) {
    text = text ? `${text}（${row.isp}）` : row.isp;
  }
  return text || '-';
}

// B8 令牌用量后台页：展示每个令牌（key）消耗的额度与调用的模型
const TokenUsage = () => {
  const { t } = useTranslation();
  const isAdminUser = isAdmin();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  // 已展开的父行 key 集合
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  let now = new Date();
  const [inputs, setInputs] = useState({
    username: '',
    token_name: '',
    start_timestamp: timestamp2string(0),
    end_timestamp: timestamp2string(now.getTime() / 1000 + 3600),
  });
  const { username, token_name, start_timestamp, end_timestamp } = inputs;

  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const loadData = async () => {
    setLoading(true);
    let localStartTimestamp = Date.parse(start_timestamp) / 1000;
    let localEndTimestamp = Date.parse(end_timestamp) / 1000;
    let url;
    if (isAdminUser) {
      url = `/api/log/token_stat?type=2&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}&username=${username}&token_name=${token_name}`;
    } else {
      url = `/api/log/self/token_stat?type=2&start_timestamp=${localStartTimestamp}&end_timestamp=${localEndTimestamp}&token_name=${token_name}`;
    }
    try {
      const res = await API.get(url);
      const { success, message, data } = res.data;
      if (success) {
        setData(data || []);
        setExpandedKeys(new Set());
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 把扁平行按 (username, token_name) 前端聚合成父行
  const groups = useMemo(() => {
    const map = new Map();
    (data || []).forEach((row) => {
      const key = `${row.user_id}||${row.username}||${row.token_name}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          user_id: row.user_id,
          username: row.username,
          token_name: row.token_name,
          total_quota: 0,
          total_requests: 0,
          children: [],
        });
      }
      const g = map.get(key);
      g.total_quota += row.quota || 0;
      g.total_requests += row.request_count || 0;
      g.children.push(row);
    });
    // 按总消耗额度倒序
    return Array.from(map.values()).sort(
      (a, b) => b.total_quota - a.total_quota
    );
  }, [data]);

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // 父行列数：展开图标 + 令牌名 + [用户名] + 总额度 + 总请求 + 模型数
  const colSpan = isAdminUser ? 6 : 5;

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>{t('token_usage.title')}</Card.Header>
          <Header as='h5' style={{ color: 'gray', marginTop: '4px' }}>
            {t('token_usage.description')}
          </Header>
          <Form>
            <Form.Group>
              <Form.Input
                fluid
                label={t('token_usage.filter.token_name')}
                size={'small'}
                width={isAdminUser ? 3 : 4}
                value={token_name}
                placeholder={t('token_usage.filter.token_name_placeholder')}
                name='token_name'
                onChange={handleInputChange}
              />
              {isAdminUser && (
                <Form.Input
                  fluid
                  label={t('token_usage.filter.username')}
                  size={'small'}
                  width={3}
                  value={username}
                  placeholder={t('token_usage.filter.username_placeholder')}
                  name='username'
                  onChange={handleInputChange}
                />
              )}
              <Form.Input
                fluid
                label={t('token_usage.filter.start_time')}
                size={'small'}
                width={4}
                value={start_timestamp}
                type='datetime-local'
                name='start_timestamp'
                onChange={handleInputChange}
              />
              <Form.Input
                fluid
                label={t('token_usage.filter.end_time')}
                size={'small'}
                width={4}
                value={end_timestamp}
                type='datetime-local'
                name='end_timestamp'
                onChange={handleInputChange}
              />
              <Form.Button
                fluid
                label={t('token_usage.filter.query')}
                size={'small'}
                width={2}
                onClick={loadData}
              >
                {t('token_usage.filter.query')}
              </Form.Button>
            </Form.Group>
          </Form>

          <Table basic={'very'} compact size='small'>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={1} />
                <Table.HeaderCell>
                  {t('token_usage.table.token_name')}
                </Table.HeaderCell>
                {isAdminUser && (
                  <Table.HeaderCell>
                    {t('token_usage.table.username')}
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell>
                  {t('token_usage.table.total_quota')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('token_usage.table.total_requests')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('token_usage.table.model_count')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {groups.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={colSpan} textAlign='center'>
                    {t('token_usage.empty')}
                  </Table.Cell>
                </Table.Row>
              ) : (
                groups.map((g) => {
                  const expanded = expandedKeys.has(g.key);
                  return (
                    <React.Fragment key={g.key}>
                      <Table.Row
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleExpand(g.key)}
                      >
                        <Table.Cell>
                          <Icon
                            name={expanded ? 'caret down' : 'caret right'}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          {g.token_name
                            ? renderColorLabel(g.token_name)
                            : '-'}
                        </Table.Cell>
                        {isAdminUser && <Table.Cell>{g.username}</Table.Cell>}
                        <Table.Cell>
                          {renderQuota(g.total_quota, t, 6)}
                        </Table.Cell>
                        <Table.Cell>{g.total_requests}</Table.Cell>
                        <Table.Cell>
                          <Label circular>{g.children.length}</Label>
                        </Table.Cell>
                      </Table.Row>
                      {expanded && (
                        <Table.Row>
                          <Table.Cell />
                          <Table.Cell colSpan={colSpan - 1}>
                            <Table basic={'very'} compact size='small'>
                              <Table.Header>
                                <Table.Row>
                                  <Table.HeaderCell>
                                    {t('token_usage.table.model_name')}
                                  </Table.HeaderCell>
                                  <Table.HeaderCell>
                                    {t('token_usage.table.quota')}
                                  </Table.HeaderCell>
                                  <Table.HeaderCell>
                                    {t('token_usage.table.requests')}
                                  </Table.HeaderCell>
                                  <Table.HeaderCell>
                                    {t('token_usage.table.prompt_tokens')}
                                  </Table.HeaderCell>
                                  <Table.HeaderCell>
                                    {t('token_usage.table.completion_tokens')}
                                  </Table.HeaderCell>
                                  {/* AlphaGate B13: 该 令牌×模型 最近一次调用的 IP + 归属地 */}
                                  <Table.HeaderCell>
                                    {t('token_usage.table.ip')}
                                  </Table.HeaderCell>
                                  <Table.HeaderCell>
                                    {t('token_usage.table.geo')}
                                  </Table.HeaderCell>
                                </Table.Row>
                              </Table.Header>
                              <Table.Body>
                                {g.children.map((c, idx) => (
                                  <Table.Row key={`${g.key}-${c.model_name}-${idx}`}>
                                    <Table.Cell>
                                      {c.model_name
                                        ? renderColorLabel(c.model_name)
                                        : '-'}
                                    </Table.Cell>
                                    <Table.Cell>
                                      {renderQuota(c.quota, t, 6)}
                                    </Table.Cell>
                                    <Table.Cell>{c.request_count}</Table.Cell>
                                    <Table.Cell>{c.prompt_tokens}</Table.Cell>
                                    <Table.Cell>
                                      {c.completion_tokens}
                                    </Table.Cell>
                                    {/* AlphaGate B13: IP + 归属地 */}
                                    <Table.Cell>{c.ip ? c.ip : '-'}</Table.Cell>
                                    <Table.Cell>{renderGeoText(c)}</Table.Cell>
                                  </Table.Row>
                                ))}
                              </Table.Body>
                            </Table>
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </Table.Body>

            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan={colSpan}>
                  <Button size='small' onClick={loadData} loading={loading}>
                    {t('token_usage.refresh')}
                  </Button>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </Card.Content>
      </Card>
    </div>
  );
};

export default TokenUsage;
